
import { z } from 'zod';
import { StoryPlanningSchema } from './storyline-planning';
import { ContinuityFactSchema } from './continuity-log';

// =============================================================================
// GENRE SCHEMAS
// =============================================================================

export const GenreCollectionSchema = z.object({
  displayName: z.string().describe('A display-friendly name for the genre collection'),
  templateIds: z.array(z.string()).describe('IDs of genre templates in this collection'),
  names: z.array(z.string()).describe('Names of the genres in this collection'),
  descriptions: z.array(z.string()).describe('Descriptions of the genres in this collection'),
});

// =============================================================================
// PLOT SNAPSHOT SCHEMA
// =============================================================================

/**
 * Plot Snapshot Schema
 * A snapshot of the plot's state at the end of a specific chapter.
 *
 * Question tracking (resolved/unresolved plot questions) has moved to the
 * weave snapshotter, which works on top of storylines, plot threads, and
 * the continuity-extractor agent. Older documents may still carry
 * `resolvedPlotQuestions`/`unresolvedPlotQuestions` (or the even older
 * `resolvedPlotThreads`/`unresolvedPlotThreads`); zod strips them on parse.
 */
export const PlotSnapshotSchema = z.object({
  id: z.string().describe("Unique identifier for this snapshot."),
  chapterId: z.string().describe("The ID of the chapter this snapshot corresponds to."),
  timestamp: z.string().describe("ISO timestamp when this snapshot was created."),
  plotSummary: z.string().describe("A neutral, chronological summary of all key events up to and including this chapter."),
  majorPlotEvents: z.array(z.object({
    id: z.string(),
    description: z.string(),
  })).optional().describe("Key plot events that occurred in this chapter."),
});

// =============================================================================
// STORY FEEDBACK SCHEMA
// =============================================================================

/**
 * Feedback from a single "hat" in the story-feedback agent output.
 * Shape mirrors story-feedback-v1's HatFeedbackSchema so the persisted value
 * can be rendered directly by the dialog without re-parsing.
 */
const StoredHatFeedbackSchema = z.object({
  headline: z.string(),
  rating: z.number(),
  body: z.string(),
  strengths: z.array(z.string()).default([]),
  concerns: z.array(z.string()).default([]),
});

/**
 * Persisted snapshot of the most recent story-feedback-v1 run. The card shows
 * a compact preview of this and lets the user reopen the dialog with the full
 * result. Only the latest run is retained.
 */
export const StoredStoryFeedbackSchema = z.object({
  generatedAt: z.string().describe('ISO timestamp of when this feedback was generated.'),
  overallImpression: z.string(),
  yellowPressCritic: StoredHatFeedbackSchema,
  developmentalEditor: StoredHatFeedbackSchema,
  publisher: StoredHatFeedbackSchema,
});

export type StoredStoryFeedback = z.infer<typeof StoredStoryFeedbackSchema>;

// =============================================================================
// DISTILLED CHANGES SCHEMA
// =============================================================================

/**
 * Shape mirrors distill-changes-v1's DistilledChangeSchema so the persisted
 * value can be rendered directly by the dialog without re-parsing.
 */
const StoredDistilledChangeSchema = z.object({
  title: z.string(),
  scope: z.enum([
    'structure',
    'storylines',
    'character_arcs',
    'characters',
    'theme',
    'stakes',
    'pacing',
    'premise',
  ]),
  priority: z.enum(['high', 'medium', 'low']),
  rationale: z.string(),
  recommendation: z.string(),
});

export type StoredDistilledChange = z.infer<typeof StoredDistilledChangeSchema>;

/**
 * Persisted snapshot of the most recent distill-changes-v1 run. The distilled
 * changes are shown as a fourth tab in the Story Feedback dialog. Only the
 * latest run is retained; regenerating replaces it.
 */
export const StoredDistilledChangesSchema = z.object({
  generatedAt: z.string().describe('ISO timestamp of when these changes were distilled.'),
  sourceFeedbackGeneratedAt: z.string().optional().describe(
    'ISO timestamp of the story feedback this distillation was derived from. Used to '
    + 'detect when the underlying feedback has been regenerated since.'
  ),
  summary: z.string(),
  changes: z.array(StoredDistilledChangeSchema).default([]),
  changeRequest: z.string().describe(
    'A natural-language change request, formatted as direct input for story-modifier-v1.'
  ),
});

export type StoredDistilledChanges = z.infer<typeof StoredDistilledChangesSchema>;

// =============================================================================
// STORY WEAKNESSES SCHEMA
// =============================================================================

/**
 * A single diagnosed weakness from story-weaknesses-v1, persisted with a stable
 * id and an `addressed` flag so the author can triage the list over time.
 * The issue fields mirror story-weaknesses-v1's WeaknessIssueSchema.
 */
const StoredWeaknessIssueSchema = z.object({
  id: z.string().describe('Stable client-generated id, used to toggle `addressed`.'),
  title: z.string(),
  severity: z.enum(['critical', 'major', 'minor']),
  fixType: z.enum(['structural', 'character', 'content']),
  chapterStart: z.number(),
  chapterEnd: z.number(),
  summary: z.string(),
  detail: z.string(),
  addressed: z.boolean().default(false).describe('Whether the author has marked this issue as handled.'),
});

export type StoredWeaknessIssue = z.infer<typeof StoredWeaknessIssueSchema>;

/**
 * Persisted snapshot of the most recent story-weaknesses-v1 run. The Weaknesses
 * card on the Story Review page renders this and lets the author mark issues as
 * addressed. Only the latest run is retained; regenerating replaces it.
 */
export const StoredStoryWeaknessesSchema = z.object({
  generatedAt: z.string().describe('ISO timestamp of when these weaknesses were analyzed.'),
  overview: z.string(),
  issues: z.array(StoredWeaknessIssueSchema).default([]),
});

export type StoredStoryWeaknesses = z.infer<typeof StoredStoryWeaknessesSchema>;

// =============================================================================
// CANONICAL STORY STRUCTURE SCHEMA
// =============================================================================

/**
 * The complete StoryStructure entity as stored in the database.
 * This is the canonical source of truth for story metadata.
 */
export const StoryStructureSchema = z.object({
  summary: z.string().nullable().optional().describe('An enriched plot summary that weaves together the core theme, central conflict, and a concise overview of the story.'),
  writtenPlot: z.string().nullable().optional().describe('The detailed, multi-paragraph plot outline in Markdown format. Uses ## headings for 7 structural phases (Initial State through Final State). Present tense, third person. This is the source of truth for the narrative.'),
  plotType: z.string().optional().describe('The type of plot structure (e.g., "Three-Act", "Hero\'s Journey")'),
  storyType: z.string().optional().describe('The type of story (e.g., "Novel", "Short Story", "Novella")'),
  projectedWordCount: z.number().optional().describe('The projected total word count for the story'),
  pov: z.string().optional().describe('The point of view style (e.g., "First Person", "Third Person Limited")'),
  tense: z.string().optional().describe('The narrative tense of the story (e.g., "Past", "Present")'),
  plotNotes: z.string().optional().describe('Author notes about the plot'),
  genreCollection: GenreCollectionSchema.optional().describe('The genre collection for this story'),
  language: z.string().optional().describe('The language the story is written in'),
  
  // Plot Snapshotting
  plotSnapshots: z.array(PlotSnapshotSchema).optional().describe("A timeline of plot summaries, captured at the end of each chapter."),
  latestPlotSnapshotId: z.string().optional().describe("The ID of the most recent plot snapshot in the timeline."),

  // Weave Snapshotting
  weaveSnapshots: z.array(z.any()).optional().describe("A timeline of continuity analysis snapshots captured after each chapter."),
  latestWeaveSnapshotId: z.string().optional().describe("The ID of the most recent weave snapshot."),

  // Continuity Log
  continuityLog: z.array(ContinuityFactSchema).optional().describe(
    "Story-level ledger of binding facts (deadlines, counts, locations, in-world dates, " +
    "promises, world rules, ongoing conditions) with lifecycles. Persisted in the " +
    "`continuityFacts` subcollection and rehydrated here on project load.",
  ),

  // Story Planning
  storyPlanning: StoryPlanningSchema.optional().describe("Storylines with plot/arc aspects and story questions."),

  // Extracted Synopsis (from import)
  extractedSynopsis: z.string().optional().describe("A condensed synopsis extracted from imported content, approximately 3% of original length."),

  // Story Feedback (persisted latest run only)
  lastStoryFeedback: StoredStoryFeedbackSchema.optional().describe("The most recent story-feedback-v1 result, persisted so the Story Analysis card can re-open it."),

  // Distilled Changes (persisted latest run only)
  lastDistilledChanges: StoredDistilledChangesSchema.optional().describe("The most recent distill-changes-v1 result, persisted so the Story Analysis card can re-open it."),

  // Story Weaknesses (persisted latest run only)
  lastStoryWeaknesses: StoredStoryWeaknessesSchema.optional().describe("The most recent story-weaknesses-v1 result, persisted so the Weaknesses card can re-render it and track addressed issues."),
});

// =============================================================================
// SUBSET SCHEMAS (For Different Use Cases)
// =============================================================================

/**
 * Minimal story structure reference for AI context.
 * Use when agents only need basic story metadata.
 */
export const StoryStructureRefSchema = z.object({
  plotType: z.string().optional().describe('The type of plot structure'),
  pov: z.string().optional().describe('The point of view style'),
  storyType: z.string().optional().describe('The type of story'),
  genre: z.string().optional().describe('The genre of the story'),
});

/**
 * Story structure context for AI agents.
 * Includes all fields relevant for narrative generation.
 */
export const StoryStructureContextSchema = z.object({
  blurb: z.string().optional().describe('The core blurb of the story'),
  genre: z.string().optional().describe('The genre of the story'),
  storyType: z.string().optional().describe('The type of story'),
  plotType: z.string().optional().describe('The type of plot structure'),
  pov: z.string().optional().describe('The point of view style'),
  tense: z.string().optional().describe('The narrative tense (e.g., Past, Present)'),
  summary: z.string().nullable().optional().describe('An enriched plot summary that weaves together the core theme, central conflict, and a concise overview of the story.'),
  writtenPlot: z.string().optional().describe('The detailed, multi-paragraph plot outline in Markdown format'),
  projectedWordCount: z.number().optional().describe('The projected total word count for the story'),
  language: z.string().optional().describe('The language the story is written in'),
});

// =============================================================================
// TYPE INFERENCE
// =============================================================================

export type StoryStructure = z.infer<typeof StoryStructureSchema>;
export type GenreCollection = z.infer<typeof GenreCollectionSchema>;
export type StoryStructureRef = z.infer<typeof StoryStructureRefSchema>;
export type StoryStructureContext = z.infer<typeof StoryStructureContextSchema>;
export type PlotSnapshot = z.infer<typeof PlotSnapshotSchema>;
